/**
 * Shared "You're saving" top-bar calculation, used by the insole popup 1 and
 * popup 2 templates (assets/insole-popup-shared.js). Both popups clone their
 * own `.popup-topbar` markup from a <template>, so this is a plain function
 * (not a custom element) that each popup calls against its own element.
 */
window.InsoleTopBar = (function () {
  async function fetchCartTotalCents() {
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' }).then((r) => r.json());
      return cart?.total_price || 0;
    } catch (e) {
      console.error('[InsoleTopBar] Failed to fetch /cart.js:', e);
      return 0;
    }
  }

  function computeState(existingCartCents, pendingCents, thresholdCents) {
    // Match assets/free-shipping.js's threshold handling exactly: the merchant-entered
    // threshold is in the shop's base currency, but cart totals are reported in whatever
    // currency the shopper is currently viewing, so it must be adjusted the same way.
    const adjustedThreshold = Math.round(thresholdCents * (window.Shopify?.currency?.rate || 1));
    const projected = existingCartCents + pendingCents;
    const isEligible = projected >= adjustedThreshold;
    const pct = adjustedThreshold > 0 ? Math.min((projected / adjustedThreshold) * 100, 100) : 100;
    return { projected, isEligible, pct, thresholdCents: adjustedThreshold };
  }

  function render(el, state, savedCents) {
    const format = window.theme?.settings?.money_with_currency_format || '${{amount}}';
    const fmt = (cents) => formatMoney(cents, format);

    const savingEl = el.querySelector('[data-topbar-saving]');
    if (savingEl) savingEl.textContent = fmt(Math.max(savedCents, 0));

    const cartEl = el.querySelector('[data-topbar-cart-total]');
    if (cartEl) cartEl.textContent = `Cart ${fmt(state.projected)}`;

    const fill = el.querySelector('[data-topbar-fill]');
    if (fill) fill.style.width = `${state.pct}%`;

    const shipEl = el.querySelector('[data-topbar-shipping-note]');
    if (shipEl) {
      if (state.isEligible) {
        shipEl.textContent = 'Free shipping applied';
        shipEl.classList.add('is-eligible');
      } else {
        const feeCents = parseInt(el.dataset.shippingFee, 10) || 0;
        shipEl.textContent = `+ ${fmt(feeCents)} shipping`;
        shipEl.classList.remove('is-eligible');
      }
    }

    const thresholdEl = el.querySelector('[data-topbar-threshold-note]');
    if (thresholdEl) thresholdEl.textContent = `Free over ${fmt(state.thresholdCents)}`;
  }

  async function peek(el, pendingCents) {
    const thresholdCents = parseInt(el.dataset.shippingThreshold, 10) || 0;
    const existingCartCents = await fetchCartTotalCents();
    return computeState(existingCartCents, pendingCents, thresholdCents);
  }

  async function update(el, { pendingCents, savedCents }) {
    const state = await peek(el, pendingCents);
    render(el, state, savedCents);
    return state;
  }

  // Cart-addon discounts (e.g. the wash bag's normal/everyday discount) are only
  // real if backed by settings.cart_addon_discounts_json — the same source the cart
  // drawer's "Goes great with your order" carousel uses (assets/free-shipping.js,
  // updateAddonCardPricing()). Shared here since both popup 1 and popup 2 need it.
  function getBagDiscountPct(discountsJson, handle) {
    try {
      const discounts = JSON.parse(discountsJson || '{}');
      return parseInt(discounts[handle], 10) || 0;
    } catch (e) {
      console.warn('[InsoleTopBar] Failed to parse cart_addon_discounts_json:', e);
      return 0;
    }
  }

  // Discount Ladder mode's top-bar renderer — a percentage-off framing instead of
  // the free-shipping progress framing. Fully synchronous: no cart/threshold fetch,
  // since this mode only cares about which discount tier is currently active.
  function renderLadder(el, state) {
    const format = window.theme?.settings?.money_with_currency_format || '${{amount}}';
    const fmt = (cents) => formatMoney(cents, format);

    const savingEl = el.querySelector('[data-topbar-saving]');
    if (savingEl) savingEl.textContent = state.pctLabel;

    const cartEl = el.querySelector('[data-topbar-cart-total]');
    if (cartEl) cartEl.textContent = `Saving ${fmt(Math.max(state.savedCents, 0))}`;

    const fill = el.querySelector('[data-topbar-fill]');
    if (fill) fill.style.width = `${state.fillPct}%`;

    const tier1El = el.querySelector('[data-topbar-shipping-note]');
    if (tier1El) {
      tier1El.textContent = state.tier1Label;
      tier1El.classList.add('is-eligible');
    }

    const tier2El = el.querySelector('[data-topbar-threshold-note]');
    if (tier2El) {
      tier2El.textContent = state.tier2Label;
      tier2El.classList.toggle('is-eligible', !!state.tier2Active);
    }
  }

  return { update, render, renderLadder, peek, computeState, fetchCartTotalCents, getBagDiscountPct };
})();
